/**
 * Vendor control-plane API (``/api/v1/admin/*``) — super-admin only.
 *
 * Mirrors the backend in ``apps/organization/platform_*.py``: pricing plans,
 * the module/function registry, and per-client entitlement overrides. Every
 * endpoint here is gated server-side by ``IsSuperAdmin``; the UI also hides
 * the console behind ``useIsSuperAdmin``.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, type Paginated } from "@/lib/api";
import {
  clearImpersonationOverlay,
  startImpersonationOverlay,
} from "@/lib/auth";
import { reloadMeAfterAuthSwitch } from "@/lib/me";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export interface RegistryModule {
  code: string;
  label: string;
  default_enabled: boolean;
  is_premium: boolean;
  /** Bare-minimum module — always on, can't be switched off. */
  is_always_on?: boolean;
}

export interface RegistryFeature {
  module_code: string;
  code: string;
  label: string;
  default_enabled: boolean;
}

export interface Registry {
  modules: RegistryModule[];
  features: RegistryFeature[];
}

export interface PlanModule {
  module_code: string;
  is_enabled: boolean;
}

export interface PlanFeature {
  module_code: string;
  feature_code: string;
  is_enabled: boolean;
}

export interface Plan {
  id: string;
  code: string;
  name: string;
  description: string;
  rank: number;
  is_active: boolean;
  is_public: boolean;
  monthly_price: string | null;
  currency: string;
  modules: PlanModule[];
  features: PlanFeature[];
  subscriber_count: number;
}

export interface ClientSummary {
  id: string;
  display_name: string;
  legal_name: string;
  abbreviation: string;
  lifecycle_state: string;
  is_active: boolean;
  plan_code: string | null;
  plan_name: string | null;
  enabled_module_count: number;
}

export interface ClientSubscription {
  plan: string;
  plan_code: string;
  plan_name: string;
  started_at: string | null;
  expires_at: string | null;
  is_current: boolean;
  notes: string;
}

export interface ModuleOverride {
  module_code: string;
  is_enabled: boolean;
  license_expires_at: string | null;
  seat_limit: number | null;
  seats_used: number;
}

export interface FeatureOverride {
  module_code: string;
  feature_code: string;
  is_enabled: boolean;
}

export interface ClientDetail extends ClientSummary {
  subscription: ClientSubscription | null;
  effective_modules: string[];
  effective_features: string[];
  module_overrides: ModuleOverride[];
  feature_overrides: FeatureOverride[];
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------
const KEYS = {
  registry: ["admin", "registry"] as const,
  clients: ["admin", "clients"] as const,
  client: (id: string) => ["admin", "clients", id] as const,
  plans: ["admin", "plans"] as const,
};

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------
export function useRegistry() {
  return useQuery<Registry>({
    queryKey: KEYS.registry,
    queryFn: () => api.get<Registry>("/admin/registry/"),
    staleTime: 30 * 60 * 1000, // registry is code-defined; rarely changes
  });
}

export function useClients(search?: string) {
  return useQuery<Paginated<ClientSummary>>({
    queryKey: [...KEYS.clients, search ?? ""],
    queryFn: () =>
      api.get<Paginated<ClientSummary>>(
        `/admin/clients/${search ? `?search=${encodeURIComponent(search)}` : ""}`,
      ),
  });
}

export function useClient(id: string | undefined) {
  return useQuery<ClientDetail>({
    queryKey: KEYS.client(id ?? ""),
    queryFn: () => api.get<ClientDetail>(`/admin/clients/${id}/`),
    enabled: Boolean(id),
  });
}

export function usePlans() {
  return useQuery<Paginated<Plan>>({
    queryKey: KEYS.plans,
    queryFn: () => api.get<Paginated<Plan>>("/admin/plans/"),
  });
}

// ---------------------------------------------------------------------------
// Mutations — all return the refreshed ClientDetail and invalidate caches.
// ---------------------------------------------------------------------------
export interface PlanWritePayload {
  code: string;
  name: string;
  description?: string;
  rank?: number;
  is_active?: boolean;
  is_public?: boolean;
  monthly_price?: string | null;
  currency?: string;
  modules: PlanModule[];
  features: PlanFeature[];
}

export function useSavePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id?: string; data: PlanWritePayload }) =>
      vars.id
        ? api.put<Plan>(`/admin/plans/${vars.id}/`, vars.data)
        : api.post<Plan>("/admin/plans/", vars.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.plans }),
  });
}

export function useDeletePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/admin/plans/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.plans }),
  });
}

function clientAction(qc: ReturnType<typeof useQueryClient>, id: string) {
  return () => {
    qc.invalidateQueries({ queryKey: KEYS.client(id) });
    qc.invalidateQueries({ queryKey: KEYS.clients });
  };
}

export function useAssignPlan(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { plan_id: string; expires_at?: string | null }) =>
      api.post<ClientDetail>(`/admin/clients/${clientId}/assign-plan/`, body),
    onSuccess: clientAction(qc, clientId),
  });
}

export function useClearPlan(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<ClientDetail>(`/admin/clients/${clientId}/clear-plan/`, {}),
    onSuccess: clientAction(qc, clientId),
  });
}

export function useResetClient(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<ClientDetail>(`/admin/clients/${clientId}/reset/`, {}),
    onSuccess: clientAction(qc, clientId),
  });
}

export function useSetModule(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      module_code: string;
      is_enabled?: boolean;
      reset?: boolean;
    }) => api.post<ClientDetail>(`/admin/clients/${clientId}/set-module/`, body),
    onSuccess: clientAction(qc, clientId),
  });
}

export function useSetFeature(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      module_code: string;
      feature_code: string;
      is_enabled?: boolean;
      reset?: boolean;
    }) => api.post<ClientDetail>(`/admin/clients/${clientId}/set-feature/`, body),
    onSuccess: clientAction(qc, clientId),
  });
}

// ---------------------------------------------------------------------------
// Impersonation
// ---------------------------------------------------------------------------
export interface ImpersonatableUser {
  id: string;
  username: string;
  display_name: string;
  is_active: boolean;
}

export interface ImpersonationGrant {
  session_id: string;
  access_token: string;
  expires_at: string;
  target_user_id: string;
  target_username: string;
}

export function useClientUsers(clientId: string | undefined) {
  return useQuery<ImpersonatableUser[]>({
    queryKey: ["admin", "clients", clientId, "users"],
    queryFn: () => api.get<ImpersonatableUser[]>(`/admin/clients/${clientId}/users/`),
    enabled: Boolean(clientId),
  });
}

/** Start impersonating a user: mints a token, installs the overlay so every
 *  subsequent request acts as that user, then resets cached queries. */
export function useStartImpersonation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { user_id: string; reason: string; minutes?: number }) =>
      api.post<ImpersonationGrant>("/admin/impersonate/", body),
    onSuccess: async (grant) => {
      startImpersonationOverlay(grant.access_token);
      // The whole app now resolves as the target user: drop the admin's
      // cross-tenant cache and refetch /me under the overlay. This flips the
      // already-mounted "acting as" banner + viewport frame in AppLayout
      // immediately (the admin console lives inside that layout) — without it
      // they only appeared after a manual reload.
      try {
        await reloadMeAfterAuthSwitch(qc);
      } catch {
        // A /me hiccup shouldn't block the session — the observer will refetch
        // on the next render / window focus, same as before this fix.
      }
    },
  });
}

/** Stop impersonating: drop the overlay (restoring the admin session) and
 *  close the audited window server-side, then refresh all queries. */
export async function stopImpersonation(
  qc: ReturnType<typeof useQueryClient>,
  sessionId: string | undefined,
) {
  clearImpersonationOverlay();
  if (sessionId) {
    try {
      await api.post("/admin/impersonate/stop/", { session_id: sessionId });
    } catch {
      // Window may have already lapsed — the overlay is gone either way.
    }
  }
  // Mirror the start path: drop the impersonated cache and refetch /me on the
  // admin's own token, so the mounted useMe observers flip impersonation → null
  // and the banner + frame disappear immediately, not after a manual reload.
  try {
    await reloadMeAfterAuthSwitch(qc);
  } catch {
    // Best-effort — the observer will refetch on the next render / focus.
  }
}
